import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import './LandingPage.css';

const PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.priceDairy.app';
const APP_STORE_URL = 'https://apps.apple.com/app/id6809188323';
const SUPPORT_EMAIL = 'support@logicalflows.com';

const LandingPage = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    setIsVisible(true);

    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="landing-page">
      <nav className={`navbar ${scrollY > 50 ? 'navbar-scrolled' : ''}`}>
        <div className="nav-container">
          <div className="logo">
            <span className="logo-icon">📊</span>
            <span className="logo-text">PriceDiary</span>
          </div>
          <div className="nav-links">
            <a href="#features" className="nav-link">
              Features
            </a>
            <a href="#about" className="nav-link">
              About
            </a>
            <Link to="/support" className="nav-link">
              Support
            </Link>
            <Link to="/terms-of-service" className="nav-link">
              Terms
            </Link>
            <Link to="/privacy-policy" className="nav-link">
              Privacy
            </Link>
          </div>
        </div>
      </nav>

      <section className="hero">
        <div className="hero-background">
          <div className="animated-shapes">
            <div className="shape shape-1"></div>
            <div className="shape shape-2"></div>
            <div className="shape shape-3"></div>
          </div>
        </div>
        <div className={`hero-content ${isVisible ? 'fade-in' : ''}`}>
          <h1 className="hero-title">
            <span className="title-line">Track.</span>
            <span className="title-line">Compare.</span>
            <span className="title-line highlight">Save.</span>
          </h1>
          <p className="hero-subtitle">
            Your smart companion for tracking prices, comparing stores, and
            saving money on every purchase.
          </p>
          <div className="hero-buttons">
            <a
              href={PLAY_STORE_URL}
              className="btn btn-primary"
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="btn-icon">📱</span>
              Download on Google Play
            </a>
            <a href="#features" className="btn btn-secondary">
              Learn More
            </a>
          </div>
        </div>
        <div className="scroll-indicator">
          <div className="mouse">
            <div className="wheel"></div>
          </div>
        </div>
      </section>

      <section id="features" className="features">
        <div className="container">
          <h2 className="section-title">Why Choose PriceDiary?</h2>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">💰</div>
              <h3>Price Tracking</h3>
              <p>
                Track product prices over time and never miss a deal. Get
                notified when prices drop.
              </p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🛒</div>
              <h3>Store Comparison</h3>
              <p>
                Compare prices across multiple stores instantly. Find the best
                deals in your area.
              </p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">📸</div>
              <h3>Receipt Scanning</h3>
              <p>
                Scan receipts automatically to record purchases. Keep track of
                your spending effortlessly.
              </p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">📍</div>
              <h3>Store Finder</h3>
              <p>
                Discover stores with the best prices near you. Save time and
                money on every shopping trip.
              </p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">👥</div>
              <h3>Community</h3>
              <p>
                Share price information with other users. Help the community
                save money together.
              </p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">📊</div>
              <h3>Insights</h3>
              <p>
                View spending patterns and price trends. Make smarter purchasing
                decisions.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section id="about" className="about">
        <div className="container">
          <div className="about-content">
            <div className="about-text">
              <h2 className="section-title">About PriceDiary</h2>
              <p>
                PriceDiary is your ultimate price tracking and comparison tool.
                We help you make informed purchasing decisions by providing
                real-time price information, store comparisons, and spending
                insights.
              </p>
              <p>
                Our mission is to help you save money on every purchase while
                making shopping more convenient and transparent.
              </p>
            </div>
            <div className="about-stats">
              <div className="stat-item">
                <div className="stat-number">10K+</div>
                <div className="stat-label">Active Users</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">50K+</div>
                <div className="stat-label">Products Tracked</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">$1M+</div>
                <div className="stat-label">Saved by Users</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <h3>PriceDiary</h3>
              <p>Track. Compare. Save.</p>
            </div>
            <div className="footer-section">
              <h4>Legal</h4>
              <Link to="/terms-of-service" className="footer-link">
                Terms of Service
              </Link>
              <Link to="/privacy-policy" className="footer-link">
                Privacy Policy
              </Link>
              <Link to="/support" className="footer-link">
                Support
              </Link>
            </div>
            <div className="footer-section">
              <h4>Contact</h4>
              <a href={`mailto:${SUPPORT_EMAIL}`} className="footer-link">
                {SUPPORT_EMAIL}
              </a>
            </div>
            <div className="footer-section">
              <h4>Download</h4>
              <a
                href={PLAY_STORE_URL}
                className="footer-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                Google Play Store
              </a>
              <a
                href={APP_STORE_URL}
                className="footer-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                App Store
              </a>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2026 PriceDiary / Logical Flows. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
